import dayjs from 'dayjs';
import { DurationUnit } from 'switch-shared/utils/duration.enum';

import { formatNumber } from '../../../../utils/formatting';

/**
 * Determines if the timeframe spans more than one day
 */
export function isMultiDayTimeframe(
  startDate: Date | string,
  endDate: Date | string
): boolean {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) return false;

  const diffHours = end.diff(start, 'hour', true);
  return diffHours > 24;
}

/**
 * Determines if the timeframe spans more than one week
 */
export function isMultiWeekTimeframe(
  startDate: Date | string,
  endDate: Date | string
): boolean {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) return false;

  const diffDays = end.diff(start, 'day', true);
  return diffDays > 7;
}

/**
 * Format timestamp based on total timeframe duration AND bucket granularity
 * Logic:
 * - Multi-week + coarse buckets (Days+): "DD/MM"
 * - Multi-day (< 1 week) + granular buckets: "DD MMM, HH:mm"
 * - Multi-day (< 1 week) + coarse buckets: "DD MMM"
 * - Single-day timeframe: "HH:mm"
 */
export function formatAxisByTimeframe(
  value: string | number,
  startDate: Date | string,
  endDate: Date | string,
  bucketUnit?: DurationUnit
): string {
  const d = dayjs(value);
  if (!d.isValid()) return String(value);

  const isMultiDay = isMultiDayTimeframe(startDate, endDate);
  const isMultiWeek = isMultiWeekTimeframe(startDate, endDate);
  const isGranularBucket =
    bucketUnit === DurationUnit.Minutes || bucketUnit === DurationUnit.Hours;

  // Multi-week with coarse buckets: show compact date only
  if (isMultiWeek) {
    return d.format('DD/MM');
  }

  // Multi-day (but < 1 week) with granular buckets: show readable date + time
  if (isMultiDay && isGranularBucket) {
    return d.format('DD MMM, HH:mm');
  }

  // Multi-day (but < 1 week) with coarse buckets: show readable date only
  if (isMultiDay) {
    return d.format('DD MMM');
  }

  // Single day: show only time
  return d.format('HH:mm');
}

/**
 * Format tooltip timestamp based on total timeframe duration
 * Tooltips always show full detail regardless of timeframe scale
 */
export function formatTooltipByTimeframe(
  value: string | number,
  startDate: Date | string,
  endDate: Date | string,
  bucketUnit?: DurationUnit
): string {
  const d = dayjs(value);
  if (!d.isValid()) return String(value);

  const isMultiDay = isMultiDayTimeframe(startDate, endDate);
  const isGranularBucket =
    bucketUnit === DurationUnit.Minutes || bucketUnit === DurationUnit.Hours;

  // For granular buckets, always show time in tooltip
  if (isGranularBucket || !isMultiDay) {
    return d.format('HH:mm, D MMMM YYYY');
  }

  // For coarse buckets in multi-day view, date is sufficient
  return d.format('D MMMM YYYY');
}

export function computeTimeAxisTicks(
  timestamps: Array<{ timestamp: string | number; ts: number }>,
  bucketOptions?: { size?: number; unit: DurationUnit }
) {
  if (!bucketOptions?.size) return timestamps.map((d) => d.ts);
  const anchorTs = dayjs(timestamps[0]?.timestamp);
  if (!anchorTs.isValid()) return timestamps.map((d) => d.ts);
  return timestamps
    .filter((point) => {
      const ts = dayjs(point.timestamp);
      if (!ts.isValid()) return false;
      switch (bucketOptions.unit) {
        case DurationUnit.Minutes: {
          const diff = ts.diff(anchorTs, 'minute');
          return ts.second() === 0 && diff % bucketOptions.size === 0;
        }
        case DurationUnit.Hours: {
          const diff = ts.diff(anchorTs, 'hour');
          return (
            ts.minute() === 0 &&
            ts.second() === 0 &&
            diff % bucketOptions.size === 0
          );
        }
        case DurationUnit.Days: {
          const diff = ts.diff(anchorTs, 'day');
          return (
            ts.hour() === 0 &&
            ts.minute() === 0 &&
            ts.second() === 0 &&
            diff % bucketOptions.size === 0
          );
        }
        case DurationUnit.Weeks: {
          const diffDays = ts
            .startOf('day')
            .diff(anchorTs.startOf('day'), 'day');
          const diffWeeks = Math.floor(diffDays / 7);
          return (
            ts.hour() === 0 &&
            ts.minute() === 0 &&
            ts.second() === 0 &&
            diffWeeks % bucketOptions.size === 0 &&
            ts.day() === anchorTs.day()
          );
        }
        case DurationUnit.Months: {
          const diff = ts.diff(anchorTs, 'month');
          return (
            ts.date() === 1 &&
            ts.hour() === 0 &&
            ts.minute() === 0 &&
            ts.second() === 0 &&
            diff % bucketOptions.size === 0
          );
        }
        case DurationUnit.Years: {
          const diff = ts.diff(anchorTs, 'year');
          return (
            ts.month() === 0 &&
            ts.date() === 1 &&
            ts.hour() === 0 &&
            ts.minute() === 0 &&
            ts.second() === 0 &&
            diff % bucketOptions.size === 0
          );
        }
        default:
          return true;
      }
    })
    .map((d) => d.ts);
}

export function computeTimeDomain(
  timestamps: Array<{ ts: number }>,
  bucketOptions?: { size?: number; unit: DurationUnit }
) {
  const ts = timestamps.map((d) => d.ts).filter((v): v is number => !!v);
  if (ts.length <= 1) return ['auto', 'auto'] as const;
  const stepMs = bucketOptions?.size ? bucketOptions.size * 60 * 1000 : 3600000;
  return [ts[0] - stepMs / 2, ts[ts.length - 1] + stepMs / 2];
}

/**
 * Format timestamp for x-axis tick labels based on bucket unit
 */
export function formatBucketAxisLabel(
  value: string | number,
  bucketOptions: { size?: number; unit: DurationUnit }
): string {
  const d = dayjs(value);
  if (!d.isValid()) return String(value);

  switch (bucketOptions.unit) {
    case DurationUnit.Minutes:
      return d.format('HH:mm');
    case DurationUnit.Hours:
      return d.hour() === 0 ? d.format('D MMM') : d.format('HH:mm');
    case DurationUnit.Days:
      return d.date() === 1 ? d.format('MMM `YY') : d.format('D MMM');
    case DurationUnit.Weeks:
      return d.format('D MMM `YY');
    case DurationUnit.Months:
      return d.format('MMM `YY');
    case DurationUnit.Years:
      return d.format('YYYY');
    default:
      return d.format('D MMM YYYY, HH:mm');
  }
}

/**
 * Format timestamp for tooltip based on bucket unit
 */
export function formatBucketTooltipLabel(
  value: string | number,
  bucketOptions: { size?: number; unit: DurationUnit }
): string {
  const d = dayjs(value);
  if (!d.isValid()) return String(value);

  if (bucketOptions.unit === DurationUnit.Days) {
    return d.format('D MMMM YYYY');
  }
  if (bucketOptions.unit === DurationUnit.Months) {
    return d.format('MMMM YYYY');
  }
  return d.format('HH:mm, D MMMM YYYY');
}

/**
 * Create x-axis formatter that handles buckets, numerics, or fallback
 *
 * @param bucketOptions - Bucket configuration for bucket-based formatting
 * @param customFormatter - Custom formatter function (takes precedence)
 * @param timeframeOptions - Optional start/end dates for timeframe-based formatting (takes precedence over bucket-based)
 */
export function createBucketAwareXFormatter(
  bucketOptions?: { size?: number; unit: DurationUnit },
  customFormatter?: (value: string | number) => string,
  timeframeOptions?: { startDate: Date | string; endDate: Date | string },
  truncateAfter?: number
): (value: string | number) => string {
  if (customFormatter) return customFormatter;

  return (value: string | number) => {
    // If timeframe options provided, use intelligent timeframe-based formatting
    // that considers bucket granularity
    if (timeframeOptions) {
      return formatAxisByTimeframe(
        value,
        timeframeOptions.startDate,
        timeframeOptions.endDate,
        bucketOptions?.unit
      );
    }
    // Otherwise fall back to bucket-based formatting
    if (bucketOptions) {
      return formatBucketAxisLabel(value, bucketOptions);
    }
    // Try numeric formatting
    const n = Number(value);
    if (Number.isFinite(n)) return formatNumber(n);
    const val = new String(value).toString();
    if (truncateAfter) {
      return `${val?.substring(0, truncateAfter) ?? 'N/A'}${val?.length > truncateAfter ? '...' : ''}`;
    }
    return String(val);
  };
}

/**
 * Create tooltip x-axis formatter that handles buckets, numerics, or fallback
 *
 * @param bucketOptions - Bucket configuration for bucket-based formatting
 * @param customFormatter - Custom formatter function (takes precedence)
 * @param timeframeOptions - Optional start/end dates for timeframe-based formatting (takes precedence over bucket-based)
 */
export function createBucketAwareTooltipXFormatter(
  bucketOptions?: { size?: number; unit: DurationUnit },
  customFormatter?: (value: string | number) => string,
  timeframeOptions?: { startDate: Date | string; endDate: Date | string }
): (value: string | number) => string {
  if (customFormatter) return customFormatter;

  return (value: string | number) => {
    // If timeframe options provided, use intelligent timeframe-based formatting
    // that considers bucket granularity
    if (timeframeOptions) {
      return formatTooltipByTimeframe(
        value,
        timeframeOptions.startDate,
        timeframeOptions.endDate,
        bucketOptions?.unit
      );
    }
    // Otherwise fall back to bucket-based formatting
    if (bucketOptions) {
      return formatBucketTooltipLabel(value, bucketOptions);
    }
    // Try numeric formatting
    const n = Number(value);
    return Number.isFinite(n) ? formatNumber(n) : String(value);
  };
}
